import React from 'react'
import { connect } from 'react-redux'
import scrollIntoView from 'scroll-into-view'
import classNames from 'classnames'
import { Link } from 'react-router-dom'

import { SearchIcon, ReturnArrowIcon } from 'components/Utils/Icons'
import NoteIcon from 'components/Notes/Icon'
import ChannelIcon from 'components/Utils/ChannelIcon'
import { LoadingLine } from 'components/Layout/LoadingLine'
import { selectors as ChannelsSelectors } from 'data/channels'
import { trackNoteClicked } from 'data/analytics/actions'

const hasHighlightedContent = path => /<\/span>/.test(path)
const formatNotePath = path => {
  if (hasHighlightedContent(path)) {
    return path
  }
  const [channel, ...parentNotes] = path.split('/')
  const [lastParentNote] = parentNotes.reverse()
  return parentNotes.length > 2 ? `${channel} / ... / ${lastParentNote}` : path
}

export default function Row({ entry, ...props }) {
  switch (entry.type) {
    case 'search': {
      const query = entry.data
      return (
        <RowLayout
          title={`‘${query}’`}
          icon={<SearchIcon size="12" />}
          path="Search inside docs"
          {...props}
          right={
            <div className="background-primary shade-100-important flexbox flexbox-align-center padding-vertical-small font-weight-medium padding-horizontal-normal radius-small">
              Search
              <ReturnArrowIcon
                className="margin-left-small opacity-normal"
                size={11}
              />
            </div>
          }
        />
      )
    }
    case 'channels': {
      return (
        <RowLayout
          title={entry.searchToken}
          path="Channel"
          icon={<ChannelIcon useNewIcons size="12" channel={entry.data} />}
          {...props}
        />
      )
    }
    case 'notes': {
      return <NoteRow entry={entry} {...props} />
    }
    default:
      return null
  }
}

function RowLayout({
  title,
  path,
  right,
  isHighlighted,
  href,
  icon,
  onClick,
  onMouseMove,
}) {
  const elementRef = React.useRef()
  // hover state is used to disabled auto scroll
  const [hovered, setHovered] = React.useState(false)
  React.useEffect(
    () => {
      if (isHighlighted && !hovered) {
        scrollIntoView(elementRef.current, { time: 0, behavior: 'smooth' })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isHighlighted]
  )
  return (
    <Link
      onMouseMove={event => {
        setHovered(true)
        onMouseMove(event)
      }}
      onMouseOut={() => {
        setHovered(false)
      }}
      ref={elementRef}
      className={classNames(
        'pointer transition shade-05',
        'background-shade-50-on-hover background-shade-50-on-focus',
        'flexbox flexbox-align-top',
        'padding-vertical-medium padding-horizontal-medium',
        {
          'background-shade-50': isHighlighted,
        }
      )}
      to={href}
      onClick={onClick}
    >
      <div style={{ marginTop: 2 }} className=" margin-right-normal flexbox">
        {icon}
      </div>
      <div
        className="flexbox flexbox-column flexbox-grow ellipsis"
        style={{ alignItems: 'baseline' }}
      >
        <span className="font-weight-medium font-size-large block">
          {title}
        </span>
        {path && (
          <span className="margin-top-smaller shade-30 font-size-smaller ellipsis block">
            {path}
          </span>
        )}
      </div>
      {right &&
        (isHighlighted || hovered) && (
          <div className="margin-left-small">{right}</div>
        )}
    </Link>
  )
}

const NoteRow = connect(
  (state, { entry }) => ({
    channel: ChannelsSelectors.getChannel(state, entry.data.channelId),
  }),
  { trackNoteClicked }
)(function NoteRow({
  entry,
  channel,
  trackNoteClicked,
  onClick = x => x,
  ...props
}) {
  const onNoteClick = React.useCallback(
    event => {
      trackNoteClicked({ source: 'search' })
      onClick(event)
    },
    [trackNoteClicked, onClick]
  )

  const fullPathHighlights =
    entry.data &&
    entry.data._snippetResult &&
    entry.data._snippetResult.fullPath

  const matchingPath =
    fullPathHighlights &&
    ['partial', 'full'].includes(fullPathHighlights.matchLevel) &&
    fullPathHighlights.value

  const fallbackPath = entry.data.fullPath || `${channel && channel.name}`
  const path = matchingPath || fallbackPath

  return (
    <RowLayout
      title={(entry.data.title && entry.data.title.trim()) || 'Untitled'}
      path={
        <span
          title={'fallbackPath'}
          dangerouslySetInnerHTML={{ __html: formatNotePath(path) }}
        />
      }
      icon={
        <NoteIcon
          iconShape={entry.data.iconShape}
          iconColor={entry.data.iconColor}
          hasChildren={entry.data.hasChildren}
        />
      }
      onClick={onNoteClick}
      {...props}
    />
  )
})

export function Loading() {
  return (
    <div className="flexbox flexbox-align-center pointer padding-large">
      <div className="flexbox flexbox-align-center shade-30 opacity-normal margin-right-normal">
        <NoteIcon iconSize="12" />
      </div>
      <div className="flexbox-grow flexbox flexbox-column">
        <LoadingLine animationDelay={0} marginBottom={4} lineWidth="80%" />
        <LoadingLine animationDelay={0.5} marginBottom={0} lineWidth="30%" />
      </div>
    </div>
  )
}
